const databaseCaches = new WeakMap();

function getDefaultFactory() {
    return typeof indexedDB === 'undefined' ? undefined : indexedDB;
}

function getDatabaseCache(factory) {
    let cache = databaseCaches.get(factory);
    if (!cache) {
        cache = new Map();
        databaseCaches.set(factory, cache);
    }
    return cache;
}

function openDatabase(factory, databaseName, storeName, version) {
    const cache = getDatabaseCache(factory);
    const cacheKey = `${databaseName}:${version}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const promise = new Promise((resolve, reject) => {
        const request = factory.open(databaseName, version);

        request.onupgradeneeded = () => {
            const database = request.result;
            if (!database.objectStoreNames.contains(storeName)) {
                database.createObjectStore(storeName);
            }
        };
        request.onsuccess = () => {
            const database = request.result;
            if (!database.objectStoreNames.contains(storeName)) {
                database.close();
                reject(new Error(
                    `IndexedDB store "${storeName}" is missing. Increase the database version to create it.`
                ));
                return;
            }
            database.onversionchange = () => {
                database.close();
                cache.delete(cacheKey);
            };
            resolve(database);
        };
        request.onerror = () => reject(request.error ?? new Error('Failed to open IndexedDB.'));
        request.onblocked = () => reject(new Error(
            `IndexedDB database "${databaseName}" is blocked by another open connection.`
        ));
    }).catch((error) => {
        cache.delete(cacheKey);
        throw error;
    });

    cache.set(cacheKey, promise);
    return promise;
}

function runTransaction(database, storeName, mode, operation) {
    return new Promise((resolve, reject) => {
        const transaction = database.transaction(storeName, mode);
        const objectStore = transaction.objectStore(storeName);
        let result;
        let request;

        try {
            request = operation(objectStore);
        } catch (error) {
            transaction.abort();
            reject(error);
            return;
        }

        if (request) {
            request.onsuccess = () => {
                result = request.result;
            };
            request.onerror = () => reject(
                request.error ?? new Error('IndexedDB request failed.')
            );
        }
        transaction.oncomplete = () => resolve(result);
        transaction.onerror = () => reject(
            transaction.error ?? new Error('IndexedDB transaction failed.')
        );
        transaction.onabort = () => reject(
            transaction.error ?? new Error('IndexedDB transaction was aborted.')
        );
    });
}

export function createIndexedDBStorage(options = {}) {
    const databaseName = options.databaseName ?? '@ryujaewan/restate';
    const storeName = options.storeName ?? 'states';
    const version = options.version ?? 1;
    const configuredFactory = options.indexedDB;

    function getFactory() {
        return configuredFactory ?? getDefaultFactory();
    }

    async function getDatabase() {
        const factory = getFactory();
        if (!factory) {
            throw new Error('IndexedDB is unavailable in this environment.');
        }
        return openDatabase(factory, databaseName, storeName, version);
    }

    return Object.freeze({
        kind: 'indexeddb',
        isAvailable: () => Boolean(getFactory()),
        async getItem(key) {
            const database = await getDatabase();
            const value = await runTransaction(
                database,
                storeName,
                'readonly',
                (store) => store.get(key)
            );
            return value === undefined ? null : value;
        },
        async setItem(key, value) {
            const database = await getDatabase();
            await runTransaction(
                database,
                storeName,
                'readwrite',
                (store) => store.put(value, key)
            );
        },
        async removeItem(key) {
            const database = await getDatabase();
            await runTransaction(
                database,
                storeName,
                'readwrite',
                (store) => store.delete(key)
            );
        },
        async clear() {
            const database = await getDatabase();
            await runTransaction(
                database,
                storeName,
                'readwrite',
                (store) => store.clear()
            );
        },
    });
}
